import React, { useEffect, useState, useRef } from 'react'
import { useSessionCleanup, useSessionStateStore } from '@renderer/stores/sessionState'
import { COUNTDOWN_PLACEHOLDER, isCountdownUrgent, useCountdown } from '@renderer/utils/countdownUtils'
import QRCode from 'qrcode'
import { useConfigStore } from '@renderer/stores/configState'
import { Button } from '@renderer/components'
import { FaStar } from 'react-icons/fa'
import { IoMail } from 'react-icons/io5'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { useTranslation } from 'react-i18next'
import ConsentModal from '@renderer/components/ConsentModal'
import UserReprintModal from '@renderer/components/UserReprintModal'
import { CongratulationsModal } from '@renderer/components/CongratulationsModal'
import EmailGalleryModal from '@renderer/components/EmailGalleryModal'
import { BackgroundContainer, GlassPanel } from '@renderer/components/BackgroundContainer'
import SessionFeedbackModal, { SessionFeedbackButton } from '@renderer/components/SessionFeedbackModal'
import { useBackgroundTextColor } from '@renderer/hooks/useBackgroundTextColor'
import { useDesignLayout, useSessionFeedback } from '@renderer/hooks'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { resolveGallerySessionUrl } from '@renderer/utils/galleryUrlUtils'
import { isDigitalMode } from '@renderer/lib/runtime'
import { DigitalResultActions } from './DigitalResultActions'

const ResultPage: React.FC = () => {
  const { t } = useTranslation()
  const {
    gallery,
    galleryKey,
    id,
    timeline,
    setSessionState,
    mode,
    logActivity,
    userPreferences
  } = useSessionStateStore()
  const sessionCleanup = useSessionCleanup()
  const { config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages.result
  const [stripPreview, setStripPreview] = useState('')
  const [videoPreview, setVideoPreview] = useState('')
  /** Only set when `videoPreview` loads metadata with a finite duration > 0 — avoids swapping hero to a broken/empty video. */
  const [validatedVideoPreview, setValidatedVideoPreview] = useState('')
  const [qrCodeImage, setQrCodeImage] = useState('')
  const [remainingTime, setRemainingTime] = useState<string>(COUNTDOWN_PLACEHOLDER)
  const [satisfactionRating, setSatisfactionRating] = useState<number | null>(
    userPreferences.satisfactionRating || null
  )
  const [hoveredStar, setHoveredStar] = useState<number | null>(null)
  /**
   * The detail behind the star. Opens itself on 1-2 stars, and stays an offer
   * above that — see lib/feedback/aspects.ts for why those six aspects.
   */
  const feedback = useSessionFeedback(satisfactionRating)
  const textColor = useBackgroundTextColor()
  const emptyStarClass =
    textColor === '#FFFFFF' ? 'text-white/40' : 'text-gray-300'
  const thankYouClass =
    textColor === '#FFFFFF' ? 'text-green-400' : 'text-green-600'

  const askUserConsent =
    config?.settings?.askUserConsent === undefined ? true : config?.settings?.askUserConsent

  const [showConsent, setShowConsent] = useState(askUserConsent)
  const [showCongratulations, setShowCongratulations] = useState(false)
  const [showReprint, setShowReprint] = useState(false)
  const [hasCheckedFirstSession, setHasCheckedFirstSession] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const hasCompleted = useRef(false)
  const isMountedRef = useRef(true)
  const autoReconstructTriggered = useRef(false)
  /** Seconds remaining when a blocking modal opened; null when the countdown isn't currently paused. */
  const pausedCountdownSecondsRef = useRef<number | null>(null)
  /** Absolute upper bound (2× resultTimeout) so a forgotten modal can't keep a kiosk pinned to Result forever. */
  const absoluteCapTimerRef = useRef<NodeJS.Timeout | null>(null)
  const absoluteCapFiredRef = useRef(false)

  const { isPortrait, isCompact, isLandscape, isSpacious } = useDesignLayout()
  /** Strip | QR pane only in landscape; portrait stacks top-to-bottom (incl. iPad portrait). */
  const resultRowLayout = isLandscape
  /** Compact phone/tablet in landscape: tighter vertical rhythm in the right pane */
  const resultLandscapeShort = isCompact && isLandscape
  /** Panel/backdrop only on the right column (landscape) or QR block (stack) when a BG image is set */
  const hasBackgroundImage = Boolean(globalConfig?.theme?.backgroundImageUrl)

  /** Prefer framed strip video only after duration metadata is valid (see validatedVideoPreview). */
  const heroIsVideo = Boolean(validatedVideoPreview)
  const resultHeadlineIsVideo = mode === 'record' || heroIsVideo
  const scanQrTypeKey = resultHeadlineIsVideo ? t('result.video', 'video') : t('result.digitalPhotos', 'digital photos')

  // --- Single inset from background edge → content (~ prior bg + inner padding, slightly roomier) ---
  const resultSurroundingPad = isCompact
    ? 'p-5 sm:p-6'
    : isSpacious && isPortrait
      ? 'p-7 sm:p-10'
      : isPortrait
        ? 'p-8 sm:p-12'
        : isSpacious
          ? 'p-6 sm:p-9'
          : 'p-6 sm:p-10'
  /** QR: spacious 1080p — keep smaller vs strip/titles (was ~280px; still scannable ~180–200px) */
  const resultQrBox = isCompact
    ? 'h-[min(20dvh,90px)] w-[min(20dvh,90px)] min-h-[72px] min-w-[72px]'
    : isSpacious && isPortrait
      ? 'h-[min(17dvh,200px)] w-[min(17dvh,200px)] min-h-[120px] min-w-[120px] max-h-[min(22dvh,200px)] max-w-[min(22dvh,200px)]'
      : isPortrait
        ? 'h-[min(24dvh,180px)] w-[min(24dvh,180px)] min-h-[120px] min-w-[120px] max-h-[min(32dvh,200px)] max-w-[min(32dvh,200px)]'
        : isSpacious && resultRowLayout
          ? 'h-[min(13dvh,200px)] w-[min(13dvh,200px)] min-h-[96px] min-w-[96px] max-h-[min(18dvh,200px)] max-w-[min(18dvh,200px)]'
          : resultRowLayout
            ? 'h-[min(20dvh,200px)] w-[min(20dvh,200px)] min-h-[80px] min-w-[80px] max-h-[min(26dvh,228px)] max-w-[min(26dvh,228px)]'
            : 'h-[min(22dvh,200px)] w-[min(22dvh,200px)] min-h-[100px] min-w-[100px]'
  const resultQrCardPad = isCompact
    ? 'p-2'
    : isSpacious
      ? 'p-4 sm:p-5'
      : isPortrait
        ? 'p-8'
        : resultRowLayout
          ? 'p-3'
          : 'p-6'
  const resultSpin = isCompact ? 'h-8 w-8' : isSpacious ? 'h-20 w-20' : isPortrait ? 'h-16 w-16' : 'h-20 w-20'
  const resultSessLabel = isCompact ? 'text-xs' : isSpacious ? 'text-xl' : isPortrait ? 'text-lg' : 'text-xl'
  const resultSatH3 = isCompact ? 'text-xs' : isSpacious ? 'text-2xl' : isPortrait ? 'text-lg' : 'text-xl'
  const resultSatP = isCompact ? 'hidden' : isSpacious ? 'text-lg' : isPortrait ? 'text-sm' : 'text-base'
  // Rating stars sized as real kiosk touch targets. The rating is the only
  // feedback mechanism and was ~33px — below the comfortable tap floor (audit).
  const resultStars = isCompact ? 'p-1 text-2xl' : isSpacious ? 'p-2 text-5xl' : isPortrait ? 'p-2 text-4xl' : 'p-2 text-4xl'
  const resultThankP = isCompact ? 'text-xs' : isSpacious ? 'text-lg' : isPortrait ? 'text-sm' : 'text-base'
  const resultTimer = isCompact
    ? 'text-xs font-mono tabular-nums'
    : isSpacious
      ? 'text-2xl font-mono tabular-nums'
      : isPortrait
        ? 'text-xl font-mono tabular-nums'
        : 'text-2xl font-mono tabular-nums'
  const showResultTimer = (config?.settings?.resultTimeout || 0) > 0
  const resultBtn = isCompact
    ? '!px-4 !py-2 !text-sm'
    : isSpacious
      ? '!px-10 !py-4 !text-xl'
      : isPortrait
        ? '!px-6 !py-3 !text-lg'
        : '!px-8 !py-4 text-xl'
  const showEmailButton =
    config?.settings?.emailEnabled === undefined || config?.settings?.emailEnabled

  const resultCountdown = useCountdown(config?.settings.resultTimeout || 120)

  // ---- Business logic (unchanged) ----

  useEffect(() => {
    const manifest = gallery?.compositionManifest
    if (
      !id ||
      manifest?.status !== 'failed' ||
      autoReconstructTriggered.current
    ) return
    autoReconstructTriggered.current = true
    const apiUrl = process.env.VITE_API_URL || ''
    fetch(`${apiUrl}/api/gallery/reconstruct-strip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: id, trigger: 'auto' })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.success && !data?.skipped) {
          logActivity('Auto-reconstruction triggered for failed strip', { sessionId: id })
        }
      })
      .catch(() => {})
  }, [id, gallery?.compositionManifest?.status, logActivity])

  const handleConsent = async (consent: boolean): Promise<void> => {
    const reprintEnabled = config?.settings?.reprintEnabled ?? false

    if (reprintEnabled) {
      setShowReprint(true)
    }

    setShowConsent(false)

    setSessionState({
      userConsent: {
        photoPublication: consent,
        consentTimestamp: new Date()
      }
    })

    logActivity(`User ${consent ? 'granted' : 'declined'} photo publication consent`, {
      consent,
      timestamp: new Date()
    })

    await new Promise((resolve) => setTimeout(resolve, 100))
    const currentState = useSessionStateStore.getState()

    if (currentState.userConsent?.photoPublication !== consent) {
      console.warn('[CONSENT] Consent value mismatch, retrying state update')
      setSessionState({
        userConsent: {
          photoPublication: consent,
          consentTimestamp: new Date()
        }
      })
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    await stateUploader.uploadSessionState(id)
    console.log('[CONSENT] Uploaded consent:', consent)

    if (reprintEnabled) {
      return
    }

    resultCountdown.start()
  }

  const handleRating = (rating: number): void => {
    if (satisfactionRating === rating) {
      setSatisfactionRating(null)
      setSessionState({
        userPreferences: {
          satisfactionRating: null,
          satisfactionTimestamp: null
        }
      })
      logActivity(`User deselected satisfaction rating`)
    } else {
      setSatisfactionRating(rating)
      const timestamp = new Date()
      setSessionState({
        userPreferences: {
          satisfactionRating: rating,
          satisfactionTimestamp: timestamp
        }
      })
      logActivity(`User rated session satisfaction: ${rating} stars`, {
        rating,
        timestamp
      })
    }
  }

  const checkFirstSessionCompletion = async (
    retryCount: number = 0,
    maxRetries: number = 3
  ): Promise<void> => {
    // The first-session congrats modal is owner-onboarding — meaningless to a
    // public web-booth guest, who is unauthenticated on this user-scoped
    // endpoint and would just get a 401. Skip it in digital mode.
    if (isDigitalMode()) {
      return
    }
    if (hasCheckedFirstSession && retryCount === 0) {
      return
    }

    try {
      if (retryCount === 0) {
        setHasCheckedFirstSession(true)
      }

      const response = await fetch(`${process.env.VITE_API_URL}/api/users/check-first-session`, {
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()

        const shouldShowModal =
          data.hasCompletedFirstSession &&
          data.firstSessionCompleted &&
          data.onboardingModalShown === false

        if (shouldShowModal) {
          setTimeout(() => {
            setShowCongratulations(true)
          }, 1000)
        } else {
          const willNeverShow =
            data.onboardingModalShown === true || data.firstSessionCompleted === true
          if (willNeverShow || retryCount >= maxRetries) {
            if (!willNeverShow && retryCount >= maxRetries) {
              console.log('[CONGRATULATIONS] Max retries reached, giving up')
            }
            return
          }
          const delayMs = 2000
          setTimeout(() => {
            checkFirstSessionCompletion(retryCount + 1, maxRetries)
          }, delayMs)
        }
      } else {
        if (retryCount < maxRetries) {
          const delayMs = 2000
          setTimeout(() => {
            checkFirstSessionCompletion(retryCount + 1, maxRetries)
          }, delayMs)
        } else {
          console.error('[CONGRATULATIONS] API call failed:', response.status, response.statusText)
        }
      }
    } catch (error) {
      console.error('[CONGRATULATIONS] Error checking first session completion:', error)
      if (retryCount < maxRetries) {
        const delayMs = 2000
        setTimeout(() => {
          checkFirstSessionCompletion(retryCount + 1, maxRetries)
        }, delayMs)
      } else {
        setHasCheckedFirstSession(true)
      }
    }
  }

  useEffect(() => {
    if (id) {
      const url = resolveGallerySessionUrl(id, globalConfig?.pages?.result?.settings)
      QRCode.toDataURL(url, {
        margin: 0,
        width: 300
      })
        .then(setQrCodeImage)
        .catch((error) => {
          console.error('[Result] Failed to generate QR code:', error)
        })
    }
  }, [id, globalConfig?.pages?.result?.settings?.galleryPublicBaseUrl])

  /** Keep strip / framed video in sync when gallery updates (e.g. video completes on Result) */
  useEffect(() => {
    const items = gallery.capturedItems || []
    const stripItem = items.find((item) => item.id?.includes('dreambooth-strip'))
    const sUrl = stripItem?.stripUrl || gallery.stripUrl || ''
    setStripPreview(sUrl)
    const withVideo = [...items].reverse().find(
      (item) => item.videoUrl && item.videoUrl.trim() !== ''
    )
    const vUrl = withVideo?.videoUrl?.trim() || ''
    setVideoPreview(vUrl)
  }, [gallery.capturedItems, gallery.stripUrl, galleryKey])

  useEffect(() => {
    if (!videoPreview) {
      setValidatedVideoPreview('')
      return
    }

    let cancelled = false
    const el = document.createElement('video')
    el.preload = 'metadata'
    el.muted = true
    el.playsInline = true

    const applyIfPlayable = (): void => {
      if (cancelled) return
      const d = el.duration
      const durationOk = Number.isFinite(d) && d > 0
      // WebCodecs / some MP4s report NaN duration until late; still allow hero <video> if we have a decoded frame.
      const hasFrame =
        el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
        el.videoWidth > 0 &&
        el.videoHeight > 0
      setValidatedVideoPreview(durationOk || hasFrame ? videoPreview : '')
    }

    const onMeta = (): void => applyIfPlayable()
    const onDurationChange = (): void => applyIfPlayable()
    const onLoadedData = (): void => applyIfPlayable()
    const onCanPlay = (): void => applyIfPlayable()
    const onError = (): void => {
      if (!cancelled) setValidatedVideoPreview('')
    }

    el.addEventListener('loadedmetadata', onMeta)
    el.addEventListener('durationchange', onDurationChange)
    el.addEventListener('loadeddata', onLoadedData)
    el.addEventListener('canplay', onCanPlay)
    el.addEventListener('error', onError)
    el.src = videoPreview

    return (): void => {
      cancelled = true
      el.removeEventListener('loadedmetadata', onMeta)
      el.removeEventListener('durationchange', onDurationChange)
      el.removeEventListener('loadeddata', onLoadedData)
      el.removeEventListener('canplay', onCanPlay)
      el.removeEventListener('error', onError)
      el.removeAttribute('src')
      el.load()
    }
  }, [videoPreview])

  useEffect(() => {
    isMountedRef.current = true

    resultCountdown.setOnTick(() => {
      if (isMountedRef.current) {
        setRemainingTime(resultCountdown.getFormattedRemainingSeconds())
      }
    })

    resultCountdown.setOnComplete(() => {
      if (hasCompleted.current && isMountedRef.current) {
        logActivity(`Result page timed out after ${config?.settings.resultTimeout || 120} seconds.`)

        const currentState = useSessionStateStore.getState()
        if (currentState.timeline.isCompleted !== true) {
          setSessionState({
            timeline: {
              isCompleted: true
            }
          })
        }

        stateUploader.uploadSessionState(id).catch((err) =>
          console.warn('[RESULT] Background upload on timeout failed:', err)
        )

        if (isMountedRef.current) {
          sessionCleanup('session_completed')
        }
      }
    })

    const reprintEnabled = config?.settings?.reprintEnabled ?? false
    if (!askUserConsent && !reprintEnabled) {
      resultCountdown.start()
    } else if (!askUserConsent && reprintEnabled) {
      setShowReprint(true)
    }

    const checkVideoProcessing = async (): Promise<void> => {
      const deadline = Date.now() + 120_000
      while (backgroundVideoProcessor.hasActiveVideoTasks() && Date.now() < deadline) {
        await new Promise<void>((r) => setTimeout(r, 400))
      }
    }

    const setupResult = async (): Promise<void> => {
      if (hasCompleted.current) return
      hasCompleted.current = true

      await checkVideoProcessing()

      const g = useSessionStateStore.getState().gallery
      const items = g.capturedItems || []
      let finalStripPreview = ''
      let finalVideoPreview = ''

      if (items.length > 0) {
        const stripItem = items.find((item) => item.id?.includes('dreambooth-strip'))
        finalStripPreview = stripItem?.stripUrl || g.stripUrl || ''
        setStripPreview(finalStripPreview)
        const videoItem = [...items].reverse().find(
          (item) => item.videoUrl && item.videoUrl.trim() !== ''
        )
        finalVideoPreview = videoItem?.videoUrl?.trim() || ''
        setVideoPreview(finalVideoPreview)
      }

      logActivity(`Session completed!`, {
        mode: mode,
        stripUrl: finalStripPreview,
        videoUrl: finalVideoPreview
      })

      const endTime = new Date()
      const duration = timeline?.startTime
        ? endTime.getTime() - new Date(timeline.startTime).getTime()
        : null

      setSessionState({
        timeline: {
          endTime: endTime,
          duration: duration,
          isCompleted: true
        }
      })

      await new Promise((resolve) => setTimeout(resolve, 100))

      const currentState = useSessionStateStore.getState()
      if (currentState.timeline.isCompleted !== true) {
        console.warn('[RESULT] isCompleted not set in state, retrying state update')
        setSessionState({
          timeline: {
            isCompleted: true,
            endTime: endTime,
            duration: duration
          }
        })
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      if (!askUserConsent) {
        await stateUploader.uploadSessionState(id)
      } else {
        console.log('[RESULT] Skipping initial upload - waiting for user consent')
      }

      try {
        await stateUploader.triggerSyncNow()
      } catch (syncError) {
        console.warn('[CONGRATULATIONS] Failed to trigger sync:', syncError)
      }

      await new Promise((resolve) => setTimeout(resolve, 6000))

      if (!hasCheckedFirstSession && navigator.onLine) {
        checkFirstSessionCompletion(0, 3)
      }
    }

    setupResult()

    return (): void => {
      isMountedRef.current = false
      resultCountdown.stop()
      resultCountdown.setOnComplete(null)
      resultCountdown.setOnTick(null)
    }
  }, [])

  /**
   * Pause the Result-page countdown while the user is interacting with a modal
   * (consent / reprint / email / congratulations). Resume from where we left off
   * when all modals are dismissed. This prevents the page from auto-navigating
   * to Welcome while a customer is reading the QR or typing their email address.
   */
  const hasBlockingModal =
    showConsent || showReprint || showEmailModal || showCongratulations || feedback.isOpen
  useEffect(() => {
    if (!showResultTimer) return
    if (hasBlockingModal) {
      const remaining = resultCountdown.getRemainingSeconds()
      // Only pause if the countdown is actually running with time on the clock.
      if (remaining > 0 && pausedCountdownSecondsRef.current === null) {
        pausedCountdownSecondsRef.current = remaining
        resultCountdown.stop()
        logActivity('Result countdown paused — blocking modal open', { remaining })
      }
    } else if (pausedCountdownSecondsRef.current !== null) {
      const resumeFrom = pausedCountdownSecondsRef.current
      pausedCountdownSecondsRef.current = null
      if (resumeFrom > 0 && !absoluteCapFiredRef.current) {
        resultCountdown.setRemainingSeconds(resumeFrom)
        resultCountdown.start()
        logActivity('Result countdown resumed', { remaining: resumeFrom })
      }
    }
  }, [hasBlockingModal, showResultTimer, resultCountdown, logActivity])

  /**
   * Absolute hard cap (2 × resultTimeout). Prevents a kiosk from being held on the
   * Result page indefinitely by a forgotten/stuck modal, while still letting a real
   * customer take their time. Mirrors the same cleanup the soft timeout performs.
   */
  useEffect(() => {
    if (!showResultTimer) return
    const resultTimeoutSec = config?.settings?.resultTimeout || 120
    const capMs = resultTimeoutSec * 2 * 1000

    absoluteCapTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current || absoluteCapFiredRef.current) return
      absoluteCapFiredRef.current = true
      logActivity(`Result page absolute cap reached after ${resultTimeoutSec * 2} seconds.`, {
        level: 'warn'
      })

      const currentState = useSessionStateStore.getState()
      if (currentState.timeline.isCompleted !== true) {
        setSessionState({ timeline: { isCompleted: true } })
      }
      stateUploader.uploadSessionState(id).catch((err) =>
        console.warn('[RESULT] Background upload on absolute cap failed:', err)
      )
      if (isMountedRef.current) {
        sessionCleanup('session_completed')
      }
    }, capMs)

    return (): void => {
      if (absoluteCapTimerRef.current) {
        clearTimeout(absoluteCapTimerRef.current)
        absoluteCapTimerRef.current = null
      }
    }
    // Run once on mount with the static config value — same lifecycle as the soft countdown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleResultBackToHome = (): void => {
    resultCountdown.stop()
    const currentState = useSessionStateStore.getState()
    if (currentState.timeline.isCompleted !== true) {
      setSessionState({ timeline: { isCompleted: true } })
    }
    stateUploader.uploadSessionState(id).catch((err) =>
      console.warn('[RESULT] Background upload failed:', err)
    )
    if (isMountedRef.current) {
      sessionCleanup('session_completed')
    }
  }

  // ---- Footer: landscape row = timer + buttons on one centered row; stack = timer above buttons ----
  const renderActionFooter = (inPanel: boolean, align: 'end' | 'center'): JSX.Element => {
    // Turn the session countdown bold red in its last 10s (shared convention);
    // urgency overrides both the neutral class and the themed inline color.
    const urgent = isCountdownUrgent(remainingTime)
    const timerColor = urgent ? '!font-bold !text-red-600' : inPanel ? 'text-carbon' : ''
    const timerStyle = urgent ? undefined : inPanel ? undefined : { color: textColor }
    const mailIcon = isCompact ? 'text-sm' : isPortrait ? 'text-2xl' : 'text-xl'

    const buttons = (
      <>
        <Button
          text={t('result.backToHome', 'Back to Home')}
          onClick={handleResultBackToHome}
          data-nav-to="welcome"
          variant="secondary"
          className={`whitespace-nowrap ${resultBtn}`}
        />
        {showEmailButton && (
          <Button
            text={
              <span className="flex items-center gap-1 whitespace-nowrap">
                <IoMail className={mailIcon} />
                {t('result.emailGallery.sendEmail', 'Send Email')}
              </span>
            }
            onClick={() => setShowEmailModal(true)}
            variant="primary"
            className={`whitespace-nowrap ${resultBtn}`}
          />
        )}
        {/*
          Digital-mode adds Download + Share next to Email. Self-gates on
          isDigitalMode() and on having a strip URL — renders nothing in
          kiosk or before the strip is ready. See DigitalResultActions.tsx.
        */}
        {id && (
          <DigitalResultActions
            stripUrl={stripPreview}
            sessionId={id}
            className={`whitespace-nowrap ${resultBtn}`}
          />
        )}
      </>
    )

    if (align === 'end') {
      const endRowGap = isSpacious
        ? 'gap-x-6 gap-y-3 sm:gap-x-10 sm:gap-y-3'
        : 'gap-x-3 gap-y-2 sm:gap-x-4'
      return (
        <div
          className={`flex w-full max-w-full shrink-0 flex-row flex-wrap items-center justify-center ${endRowGap}`}
          role="region"
          aria-label={t('result.actionsRegionLabel', 'Session actions')}
        >
          {showResultTimer && (
            <span className={`${resultTimer} ${timerColor} shrink-0`} style={timerStyle}>
              {remainingTime}
            </span>
          )}
          {buttons}
        </div>
      )
    }

    return (
      <div
        className="flex w-full shrink-0 flex-col items-center gap-2"
        role="region"
        aria-label={t('result.actionsRegionLabel', 'Session actions')}
      >
        {showResultTimer && (
          <span className={`${resultTimer} ${timerColor}`} style={timerStyle}>
            {remainingTime}
          </span>
        )}
        <div className="flex flex-wrap items-center justify-center gap-2">{buttons}</div>
      </div>
    )
  }

  /** Shared inner layout: strip + side column; with BG image, one glass shell wraps all content + shared inset. */
  const renderResultMain = (): JSX.Element => {
    /**
     * Strip column: flex-none + portrait strip aspect 4:6 (width:height = 2:3) so width tracks height
     * and we do not split the row 50/50 (avoids a huge empty channel between strip and QR pane).
     */
    const stripPaneRow =
      'flex h-full min-h-0 max-h-full flex-none shrink-0 items-center justify-center p-2 sm:p-3'
    /** In glass: explicit inset so the strip does not sit on the frosted edge (outer pad is on the wrapper too) */
    const stripPaneRowInGlass =
      'flex h-full min-h-0 max-h-full flex-none shrink-0 items-center justify-center p-3 sm:p-5 lg:p-6'
    const stripAspectBoxRow =
      'box-border flex h-full max-h-full min-h-0 w-auto shrink-0 items-center justify-center [aspect-ratio:2/3] max-w-[min(38vw,400px)] sm:max-w-[min(36vw,420px)] [&_img]:max-h-full [&_img]:max-w-full [&_img]:object-contain [&_video]:max-h-full [&_video]:max-w-full [&_video]:object-contain'
    const stripPaneStack = isCompact
      ? 'flex min-h-0 w-full max-w-full shrink-0 items-center justify-center p-2 sm:p-3 max-h-[min(46dvh,360px)]'
      : isSpacious
        ? 'flex min-h-0 w-full max-w-full shrink-0 items-center justify-center p-3 sm:p-0 max-h-[min(50dvh,560px)]'
        : 'flex min-h-0 w-full max-w-full shrink-0 items-center justify-center p-3 sm:p-0 max-h-[min(48dvh,420px)]'
    const stripPaneStackInGlass = isCompact
      ? 'flex min-h-0 w-full max-w-full shrink-0 items-center justify-center max-h-[min(46dvh,360px)]'
      : isSpacious
        ? 'flex min-h-0 w-full max-w-full shrink-0 items-center justify-center max-h-[min(50dvh,560px)]'
        : 'flex min-h-0 w-full max-w-full shrink-0 items-center justify-center max-h-[min(48dvh,420px)]'

    const paneTitleH1 = (inPanel: boolean): string => {
      const color = inPanel ? 'text-carbon' : ''
      const size = isCompact
        ? resultRowLayout
          ? 'mb-1 text-xl'
          : 'mb-1.5 text-2xl sm:text-3xl'
        : isSpacious
          ? resultRowLayout
            ? 'mb-2 text-4xl sm:text-5xl sm:whitespace-nowrap'
            : 'mb-2 text-4xl sm:text-5xl'
          : resultRowLayout
            ? 'mb-2 text-3xl sm:text-4xl sm:whitespace-nowrap'
            : 'mb-1.5 text-3xl sm:text-4xl'
      return `font-bold leading-tight ${color} ${size}`
    }
    const paneTitleH1Style = (inPanel: boolean): React.CSSProperties | undefined =>
      inPanel ? undefined : { color: textColor }
    const paneTitleSub = (inPanel: boolean): string => {
      const color = inPanel ? 'text-carbon/80' : ''
      const size = isCompact
        ? resultRowLayout
          ? 'text-xs leading-snug sm:text-sm'
          : 'text-sm leading-snug sm:text-base'
        : isSpacious
          ? resultRowLayout
            ? 'text-base leading-snug sm:text-lg lg:text-xl'
            : 'text-lg leading-snug sm:text-2xl'
          : resultRowLayout
            ? 'text-sm leading-snug sm:text-base lg:text-lg'
            : 'text-base leading-snug sm:text-xl'
      return `${size} ${color}`
    }
    const paneTitleSubStyle = (inPanel: boolean): React.CSSProperties | undefined =>
      inPanel ? undefined : { color: textColor, opacity: 0.85 }

    /** QR always above ratings, centered; short landscape needs tighter vertical rhythm */
    const qrRatingsRowClass = `flex w-full flex-col items-center ${
      resultRowLayout && resultLandscapeShort
        ? 'gap-2 sm:gap-3'
        : resultRowLayout
          ? 'gap-4 sm:gap-5'
          : 'gap-3 sm:gap-4'
    }`

    const rightPanePad = resultLandscapeShort
      ? 'p-3 sm:p-4'
      : isSpacious && resultRowLayout
        ? 'p-6 sm:p-8 lg:p-10'
        : resultRowLayout
          ? 'p-5 sm:p-7'
          : isSpacious
            ? 'px-4 pb-4 pt-3 sm:px-6 sm:pb-6'
            : 'px-6 pb-6 pt-4 sm:px-8 sm:pb-8'

    const glassPanelSoftShadow = {
      boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12)'
    } as React.CSSProperties
    /** Padding inside the frosted glass — landscape needs generous inset so content is not flush to the border */
    const resultGlassInnerPad = isCompact
      ? 'p-3 sm:p-4 md:p-5'
      : isSpacious && isPortrait
        ? 'px-8 py-6 sm:px-10 sm:py-8'
        : isPortrait
          ? 'px-10 py-8 sm:px-12 sm:py-10'
          : isSpacious
            ? 'px-8 py-6 sm:px-12 sm:py-10'
            : 'px-8 py-6 sm:px-10 sm:py-10'
    /** Hug content width; center in the page shell — avoids empty glass margins on wide screens */
    const resultGlassShellClass = isCompact
      ? 'flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden !shadow-none'
      : 'flex min-h-0 min-w-0 max-w-fit flex-col self-center overflow-hidden !shadow-none'

    const renderHero = (): JSX.Element | null => {
      const heroClass =
        'block h-auto w-auto max-h-full max-w-full object-contain object-center rounded-lg shadow-md shadow-black/15'
      if (heroIsVideo && validatedVideoPreview) {
        return (
          <video
            src={validatedVideoPreview}
            autoPlay
            loop
            muted
            playsInline
            className={heroClass}
          />
        )
      }
      if (stripPreview) {
        return (
          <img
            src={stripPreview}
            alt={t('result.photoStripAlt', 'Photo Strip')}
            className={heroClass}
          />
        )
      }
      return null
    }

    const renderTitlesInPane = (inPanel: boolean): JSX.Element => (
      <div
        className={`w-full max-w-full shrink-0 text-center ${resultRowLayout && !isCompact ? 'min-w-0 px-1 sm:px-0' : ''}`}
      >
        <h1 className={paneTitleH1(inPanel)} style={paneTitleH1Style(inPanel)}>
          {resultHeadlineIsVideo
            ? t('result.videoReady', 'Your Video Is Ready!')
            : t('result.photosReady', 'Your Photos Are Ready!')}
        </h1>
        <p className={`${paneTitleSub(inPanel)} my-2`} style={paneTitleSubStyle(inPanel)}>
          {t('result.scanQrInstruction', 'Scan the QR code below to access your {{type}}', {
            type: scanQrTypeKey
          })}
        </p>
      </div>
    )

    const renderQrAndRatings = (inPanel: boolean): JSX.Element => {
      const qrCodeCard = (
        <div
          className={`flex shrink-0 items-center justify-center bg-white shadow-md shadow-black/10 ring-1 ring-black/5 ${isCompact ? 'rounded-xl' : 'rounded-2xl'} ${resultQrCardPad}`}
        >
          {qrCodeImage ? (
            <img className={resultQrBox} src={qrCodeImage} alt={t('result.qrCodeAlt', 'QR Code')} />
          ) : (
            <div className={`${resultQrBox} flex items-center justify-center`}>
              <svg
                className={`${resultSpin} animate-spin ${inPanel ? 'text-carbon/40' : ''}`}
                style={inPanel ? undefined : { color: textColor, opacity: 0.45 }}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          )}
        </div>
      )

      const sessionLine = (
        <p
          className={`max-w-[min(100%,14rem)] shrink-0 break-all text-center font-mono tabular-nums tracking-tight ${resultSessLabel} ${inPanel ? 'text-carbon/70' : ''}`}
          style={inPanel ? undefined : { color: textColor, opacity: 0.75 }}
        >
          {id}
        </p>
      )

      const qrColumn = (
        <div className="flex shrink-0 flex-col items-center gap-2">
          {qrCodeCard}
          {sessionLine}
        </div>
      )

      const ratingsTextAlign = 'text-center'
      const ratingsBlock = (
        <div
          className="flex w-full max-w-sm min-w-0 flex-col items-center"
          role="region"
          aria-label={t('result.feedbackRegionLabel', 'QR code and feedback')}
        >
          <h3
            className={`w-full font-semibold ${ratingsTextAlign} ${resultSatH3} ${isCompact ? 'mb-1' : 'mb-1.5'} ${inPanel ? 'text-carbon' : ''}`}
            style={inPanel ? undefined : { color: textColor }}
          >
            {t('result.satisfaction.question')}
          </h3>
          <p
            className={`w-full ${ratingsTextAlign} ${resultSatP} ${isCompact ? '' : 'mb-2 sm:mb-2.5'} ${inPanel ? 'text-carbon/70' : ''}`}
            style={inPanel ? undefined : { color: textColor, opacity: 0.72 }}
          >
            {t('result.satisfaction.rateExperience')}
          </p>
          <div className="flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => handleRating(star)}
                onMouseEnter={() => setHoveredStar(star)}
                onMouseLeave={() => setHoveredStar(null)}
                className={`transition-all duration-200 hover:scale-110 ${resultStars}`}
                title={t('result.satisfaction.starAlt', { rating: star })}
              >
                <FaStar
                  className={`${
                    (hoveredStar && star <= hoveredStar) ||
                    (satisfactionRating && star <= satisfactionRating)
                      ? 'text-yellow-400'
                      : inPanel
                        ? 'text-carbon/45'
                        : emptyStarClass
                  } transition-colors duration-200`}
                />
              </button>
            ))}
          </div>
          {satisfactionRating && (
            <p
              className={`mt-1.5 w-full font-medium ${ratingsTextAlign} ${resultThankP} ${inPanel ? 'text-green-600' : thankYouClass}`}
            >
              {feedback.submitted
                ? t('result.feedback.thanks')
                : t('result.satisfaction.thankYou')}
            </p>
          )}
          {satisfactionRating && (
            <SessionFeedbackButton
              onClick={feedback.open}
              submitted={feedback.submitted}
              className={`mt-1 ${resultThankP} ${inPanel ? 'text-carbon/70' : ''}`}
            />
          )}
        </div>
      )

      return (
        <div className={qrRatingsRowClass}>
          {qrColumn}
          {ratingsBlock}
        </div>
      )
    }

    /** Optional titles + QR/ratings; stack uses includeTitles false (titles above strip). */
    const renderRightColumnBody = (inPanel: boolean, includeTitles: boolean): JSX.Element => (
      <div
        className={`flex w-full min-w-0 flex-col items-center ${
          resultRowLayout && resultLandscapeShort
            ? 'gap-2 sm:gap-3'
            : resultRowLayout && isSpacious
              ? 'gap-6 sm:gap-8'
              : resultRowLayout
                ? 'gap-4 sm:gap-6'
                : 'gap-4 sm:gap-5'
        }`}
      >
        {includeTitles ? renderTitlesInPane(inPanel) : null}
        {renderQrAndRatings(inPanel)}
      </div>
    )

    const stripHeroWrapStack = isSpacious
      ? 'flex min-h-0 w-full max-w-full items-center justify-center [&_img]:max-h-[min(50dvh,560px)] [&_video]:max-h-[min(50dvh,560px)]'
      : 'flex min-h-0 w-full max-w-full items-center justify-center [&_img]:max-h-[min(46dvh,360px)] [&_video]:max-h-[min(46dvh,360px)] sm:[&_img]:max-h-[min(48dvh,420px)] sm:[&_video]:max-h-[min(48dvh,420px)]'

    const rightPaneScrollClass =
      'custom-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain'

    const renderRowRightPane = (): JSX.Element => {
      const inPanel = hasBackgroundImage
      const rowRightMiddle = (
        <>
          {renderRightColumnBody(inPanel, true)}
          <div
            className={`w-full max-w-full shrink-0 ${
              resultLandscapeShort
                ? 'mt-4 sm:mt-5'
                : isSpacious
                  ? 'mt-10 sm:mt-14'
                  : 'mt-8 sm:mt-10'
            }`}
          >
            {renderActionFooter(inPanel, 'end')}
          </div>
        </>
      )
      const flow = (
        <div
          className={`flex min-h-0 w-full min-w-0 flex-col items-stretch ${rightPaneScrollClass} ${rightPanePad}`}
        >
          {/*
           1fr / auto / 1fr grid: vertically centers the block when shorter than the viewport; when
            content is taller, the fr rows collapse to 0 and scroll starts at the top (no top crop).
          */}
          <div className="grid min-h-full w-full grid-rows-[1fr_auto_1fr]">
            <div className="min-h-0 min-w-0" aria-hidden />
            <div className="flex w-full min-w-0 max-w-full flex-col items-center">{rowRightMiddle}</div>
            <div className="min-h-0 min-w-0" aria-hidden />
          </div>
        </div>
      )

      /** min-w-0: flex default min-width:auto prevents shrinking; 0 allows this pane to shrink & scroll instead of forcing overflow */
      const shellClass = hasBackgroundImage
        ? 'flex min-h-0 min-w-0 flex-1 basis-0 flex-col overflow-hidden sm:min-w-[16rem] md:min-w-[20rem]'
        : 'flex min-h-0 min-w-0 flex-1 basis-0 flex-col overflow-hidden py-2 sm:py-3 sm:min-w-[16rem] md:min-w-[20rem]'

      return <div className={shellClass}>{flow}</div>
    }

    if (resultRowLayout) {
      const rowRegionLabel = t('result.previewQrRegionLabel', 'Photo preview and QR code')
      const rowStripClass = hasBackgroundImage ? stripPaneRowInGlass : stripPaneRow
      const rowGap =
        isSpacious && !resultLandscapeShort
          ? 'gap-10 sm:gap-12 lg:gap-14'
          : 'gap-3 sm:gap-4 md:gap-5'
      const rowBody = (
        <div className={`flex min-h-0 min-w-0 flex-1 flex-row items-stretch justify-center ${rowGap}`}>
          <div className={rowStripClass}>
            <div className={stripAspectBoxRow}>{renderHero()}</div>
          </div>
          {renderRowRightPane()}
        </div>
      )

      return (
        <div
          className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col items-center justify-center overflow-hidden lg:max-w-7xl"
          role="region"
          aria-label={rowRegionLabel}
        >
          {hasBackgroundImage ? (
            <GlassPanel className={resultGlassShellClass} style={glassPanelSoftShadow}>
              <div className={`flex min-h-0 min-w-0 flex-1 flex-col ${resultGlassInnerPad}`}>{rowBody}</div>
            </GlassPanel>
          ) : (
            rowBody
          )}
        </div>
      )
    }

    const stackTitlesPad = isCompact ? 'px-4' : 'px-4 sm:px-6'

    /** QR + ratings + actions (no own scroll — stack uses one page-level scroller) */
    const stackLowerBlock = (inPanel: boolean): JSX.Element => (
      <div className={`flex w-full min-w-0 flex-col items-center ${inPanel ? '' : rightPanePad}`}>
        <div className="flex w-full flex-col items-center px-1">
          {renderRightColumnBody(inPanel, false)}
          <div className="mt-8 w-full shrink-0 sm:mt-10">
            {renderActionFooter(inPanel, 'center')}
          </div>
        </div>
      </div>
    )

    /** Portrait stack: same1fr/auto/1fr centering as row right pane; single page scroll when needed */
    const stackPageScrollClass =
      'custom-scrollbar flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain'

    const stackRegionLabel = t('result.previewQrRegionLabel', 'Photo preview and QR code')
    const stackStripShell = hasBackgroundImage ? stripPaneStackInGlass : stripPaneStack

    const stackMiddle = (
      <div className="flex w-full max-w-full flex-col items-center gap-2 sm:gap-3">
        <div className={`w-full shrink-0 text-center ${hasBackgroundImage ? '' : stackTitlesPad}`}>
          {renderTitlesInPane(hasBackgroundImage)}
        </div>
        <div className={stackStripShell}>
          <div className={stripHeroWrapStack}>{renderHero()}</div>
        </div>
        <div className="w-full max-w-full shrink-0">{stackLowerBlock(hasBackgroundImage)}</div>
      </div>
    )

    return (
      <div className={stackPageScrollClass} role="region" aria-label={stackRegionLabel}>
        <div className="grid min-h-full w-full grid-rows-[1fr_auto_1fr]">
          <div className="min-h-0 min-w-0" aria-hidden />
          <div className="flex w-full min-w-0 max-w-full flex-col items-center px-0">
            {hasBackgroundImage ? (
              <GlassPanel className={`${resultGlassShellClass} shrink-0`} style={glassPanelSoftShadow}>
                <div className={`flex w-full min-w-0 max-w-full flex-col ${resultGlassInnerPad}`}>
                  {stackMiddle}
                </div>
              </GlassPanel>
            ) : (
              stackMiddle
            )}
          </div>
          <div className="min-h-0 min-w-0" aria-hidden />
        </div>
      </div>
    )
  }

  // ---- Render ----
  return (
    <>
      {askUserConsent && (
        <ConsentModal
          isVisible={showConsent}
          onConsent={handleConsent}
          stripPreview={heroIsVideo ? validatedVideoPreview : stripPreview}
          mode={heroIsVideo ? 'record' : mode}
        />
      )}

      {config?.settings?.reprintEnabled && (
        <UserReprintModal
          isVisible={showReprint}
          onComplete={() => {
            setShowReprint(false)
            resultCountdown.start()
          }}
          onSkip={() => {
            setShowReprint(false)
            resultCountdown.start()
          }}
          stripPreview={heroIsVideo ? validatedVideoPreview : stripPreview}
          stripPreviewIsVideo={heroIsVideo}
          timeout={config?.settings?.reprintTimeout ?? 60}
        />
      )}

      <CongratulationsModal
        isOpen={showCongratulations}
        onClose={async () => {
          try {
            const response = await fetch(
              `${process.env.VITE_API_URL}/api/users/update-onboarding`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  onboardingStep: 'completed',
                  onboardingCompleted: true,
                  onboardingModalShown: true
                }),
                credentials: 'include'
              }
            )
            if (response.ok) {
              console.log('[CONGRATULATIONS] Modal closed - marked onboarding as completed and modal as shown')
            } else {
              console.error('[CONGRATULATIONS] Failed to mark onboarding as completed:', response.status, response.statusText)
            }
          } catch (error) {
            console.error('[CONGRATULATIONS] Error marking onboarding as completed:', error)
          }
          setShowCongratulations(false)
        }}
      />

      {showEmailModal && id && (
        <EmailGalleryModal sessionId={id} onClose={() => setShowEmailModal(false)} />
      )}

      <SessionFeedbackModal
        isOpen={feedback.isOpen}
        rating={satisfactionRating}
        answers={feedback.answers}
        onAnswer={feedback.setAnswer}
        answeredCount={feedback.answeredCount}
        onSubmit={feedback.submit}
        onClose={feedback.close}
      />

      {!showConsent && !showReprint && (
        <BackgroundContainer className="relative flex h-full min-h-0 items-center justify-center overflow-hidden">
          <div
            className={`flex h-full max-h-full w-full max-w-full min-h-0 flex-col items-stretch justify-center overflow-hidden ${resultSurroundingPad}`}
          >
            {renderResultMain()}
          </div>
        </BackgroundContainer>
      )}
    </>
  )
}

export default ResultPage
